/**
 * Mock fixtures for the dashboard preview/demo route. The production dashboard
 * adapts real API data via useDashboardData(); these fixtures back the design
 * iteration route at /dashboard/_preview/:section and a few aspirational
 * sections (integrations grid, agent templates, resources) that are still
 * shipping as static content.
 */

export type DashboardOwner = {
  firstName: string;
  lastName: string;
  email: string;
  bookCallUrl: string;
};

export const OWNER: DashboardOwner = {
  firstName: "Sarah",
  lastName: "Chen",
  email: "sarah@northwindcoaching.com",
  bookCallUrl: "https://calendar.app.google/HFTGbpEpXb2eNUwJ6",
};

// Display label derived from the backend tier code (tier_definition table);
// open-ended because admins can create new tiers.
export type DashboardTier = string;

export type DashboardInstance = {
  id: string;
  name: string;
  agentName: string;
  status: "running" | "provisioning";
  uptimeDays: number;
  tier: DashboardTier;
  coachingSessionsRemaining: number;
  // IP-353: comp/unlimited-entitlements instance (off the usage limits).
  unlimitedEntitlements?: boolean;
  region?: "ap-southeast-2" | "us-east-1" | "eu-west-1";
  diskUsedGb?: number;
  diskTotalGb?: number;
  cpuLoad?: "light" | "moderate" | "warm" | "hot";
  lastSecurityCheck?: string;
  // True while admin hasn't unlocked the instance yet. UI overlays a
  // frosted state with a "we're getting it ready" message + book-call
  // CTA, and disables the proxy open-instance link.
  locked?: boolean;
  // Caller's role on this instance + whether the owner has the
  // instance_sharing flag. Drive the hero's Share surface.
  role?: "OWNER" | "MANAGER" | "MEMBER";
  sharingEnabled?: boolean;
  // Server-computed: owner has openrouter_key_self_serve AND the tenant
  // runs on OpenRouter. Drives the AI-credits gear (with role != MEMBER).
  openrouterKeyConfigurable?: boolean;
  // Server-computed: owner has chatgpt_self_serve. Drives the "switch to
  // ChatGPT" affordance under the KPI strip (with role != MEMBER).
  chatgptSelfServeEnabled?: boolean;
  // Agent already runs on the customer's ChatGPT subscription.
  chatgptLinked?: boolean;
  aiMode?: AiMode;
  // Which ChatGPT account is paying, shown under the AI cell.
  chatgptAccount?: string | null;
};

export const INSTANCES: DashboardInstance[] = [
  {
    id: "i-northwind",
    name: "Northwind Coaching",
    agentName: "Atlas",
    status: "running",
    region: "ap-southeast-2",
    uptimeDays: 14,
    diskUsedGb: 38,
    diskTotalGb: 100,
    cpuLoad: "light",
    lastSecurityCheck: "2026-05-09T03:12:00Z",
    tier: "Accelerate",
    coachingSessionsRemaining: 2,
  },
  {
    id: "i-lighthouse",
    name: "Lighthouse Property",
    agentName: "Beacon",
    status: "running",
    region: "us-east-1",
    uptimeDays: 6,
    diskUsedGb: 14,
    diskTotalGb: 50,
    cpuLoad: "light",
    lastSecurityCheck: "2026-05-08T22:40:00Z",
    tier: "Explore",
    coachingSessionsRemaining: 1,
  },
  {
    id: "i-beacon-studio",
    name: "Beacon Studio",
    agentName: "Aurora",
    status: "provisioning",
    region: "eu-west-1",
    uptimeDays: 0,
    diskUsedGb: 0,
    diskTotalGb: 100,
    cpuLoad: "light",
    lastSecurityCheck: "2026-05-09T05:55:00Z",
    tier: "Pro",
    coachingSessionsRemaining: 0,
  },
];

export type DashboardSpend = {
  spentUsd: number;
  limitUsd: number | null;
  periodLabel: string;
};

export type DashboardConcierge = {
  teamLabel: string;
  sessionsRemaining: number;
  sessionsTotal: number;
  bookCallUrl: string;
  supportEmail: string;
  // IP-353: comp/unlimited-entitlements instance -> "Unlimited coaching
  // sessions" in place of a finite balance.
  unlimited: boolean;
};

export const CONCIERGE: DashboardConcierge = {
  teamLabel: "ZeroToClaw team",
  sessionsRemaining: 2,
  sessionsTotal: 3,
  bookCallUrl: "https://calendar.app.google/HFTGbpEpXb2eNUwJ6",
  supportEmail: "support@zerotoclaw.ai",
  unlimited: false,
};

export type SecurityCheck = {
  label: string;
  status: "ok" | "watching";
  lastCheckedHoursAgo: number;
};

export const SECURITY: SecurityCheck[] = [
  { label: "Encrypted at rest", status: "ok", lastCheckedHoursAgo: 6 },
  { label: "Network isolated", status: "ok", lastCheckedHoursAgo: 6 },
  { label: "Backups verified", status: "ok", lastCheckedHoursAgo: 12 },
  { label: "No suspicious activity", status: "ok", lastCheckedHoursAgo: 1 },
];

// Per-tenant integration state has moved to the backend
// (/api/integrations/{tenantId}). The shared `Integration` /
// `IntegrationStatus` types live in ../../types/integration. The fixtures
// below back the design preview route (/dashboard/_preview/:section) and
// stay co-located with the rest of the dashboard mocks.
export type {
  Integration,
  IntegrationStatus,
} from "../../types/integration";

import type { Integration } from "../../types/integration";
import type { AiMode } from "../../types/openclaw";

export type InSetupHandover = {
  slug: string;
  name: string;
  bookCallUrl: string;
};

export const HANDOVER: InSetupHandover = {
  slug: "twilio",
  name: "Twilio",
  bookCallUrl: "https://calendar.app.google/HFTGbpEpXb2eNUwJ6",
};

export const INTEGRATIONS_FIXTURE: Integration[] = [
  {
    slug: "telegram",
    title: "Telegram",
    category: "Messaging",
    description: "Chat with your agent from any device.",
    status: "active",
    featured: true,
    included: true,
    monthlyCost: 0,
    oneTimeCost: 0,
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "whatsapp",
    title: "WhatsApp",
    category: "Messaging",
    description: "Pair a number, message your agent like a friend.",
    status: "active",
    featured: true,
    included: true,
    monthlyCost: 0,
    oneTimeCost: 0,
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "slack",
    title: "Slack",
    category: "Messaging",
    description: "Drop your agent into team channels.",
    status: "available",
    featured: true,
    included: true,
    monthlyCost: 0,
    oneTimeCost: 0,
    iconBrandHex: "4A154B",
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "google",
    title: "Google Workspace",
    category: "Productivity",
    description: "Mail, Calendar, and Drive on tap.",
    status: "active",
    featured: true,
    included: true,
    monthlyCost: 0,
    oneTimeCost: 0,
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "elevenlabs",
    title: "ElevenLabs",
    category: "Voice",
    description: "Give your agent a real-sounding voice.",
    status: "available",
    featured: true,
    included: false,
    monthlyCost: 29,
    oneTimeCost: 0,
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "twilio",
    title: "Twilio",
    category: "Voice",
    description: "Inbound + outbound phone calls.",
    status: "provisioning",
    featured: true,
    included: false,
    monthlyCost: 15,
    oneTimeCost: 0,
    iconBrandHex: "F22F46",
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "gohighlevel",
    title: "Go High Level",
    category: "CRM",
    description: "All-in-one funnel + CRM automations.",
    status: "available",
    featured: true,
    included: false,
    monthlyCost: 0,
    oneTimeCost: 0,
    iconBrandHex: "1F4FFF",
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "xero",
    title: "Xero",
    category: "Finance",
    description: "Invoices, bills, and reconciliations.",
    status: "available",
    featured: true,
    included: false,
    monthlyCost: 0,
    oneTimeCost: 0,
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "discord",
    title: "Discord",
    category: "Messaging",
    description: "Bot your agent into community servers.",
    status: "available",
    featured: false,
    included: true,
    monthlyCost: 0,
    oneTimeCost: 0,
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "microsoft365",
    title: "Microsoft 365",
    category: "Productivity",
    description: "Outlook, Teams, OneDrive in one move.",
    status: "available",
    featured: false,
    included: true,
    monthlyCost: 0,
    oneTimeCost: 0,
    iconBrandHex: "D83B01",
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "metaads",
    title: "Meta Ads",
    category: "Marketing",
    description: "Manage Facebook + Instagram ad spend.",
    status: "available",
    featured: false,
    included: false,
    monthlyCost: 19,
    oneTimeCost: 0,
    iconBrandHex: "1877F2",
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "todoist",
    title: "Todoist",
    category: "Productivity",
    description: "Sync tasks both ways.",
    status: "available",
    featured: false,
    included: true,
    monthlyCost: 0,
    oneTimeCost: 0,
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "stripe",
    title: "Stripe",
    category: "Finance",
    description: "Customers, subs, and refunds.",
    status: "available",
    featured: false,
    included: false,
    monthlyCost: 9,
    oneTimeCost: 0,
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "hubspot",
    title: "HubSpot",
    category: "CRM",
    description: "Contacts, deals, sequences.",
    status: "available",
    featured: false,
    included: false,
    monthlyCost: 0,
    oneTimeCost: 0,
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "calendly",
    title: "Calendly",
    category: "Scheduling",
    description: "Book directly into the right calendar.",
    status: "available",
    featured: false,
    included: true,
    monthlyCost: 0,
    oneTimeCost: 0,
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "notion",
    title: "Notion",
    category: "Knowledge",
    description: "Read and write your team wiki.",
    status: "available",
    featured: false,
    included: true,
    monthlyCost: 0,
    oneTimeCost: 0,
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
  {
    slug: "apollo",
    title: "Apollo",
    category: "Sales",
    description: "Prospect databases and outreach.",
    status: "available",
    featured: false,
    included: false,
    monthlyCost: 0,
    oneTimeCost: 0,
    iconBrandHex: "0B57D0",
    connectionStatus: null,
    authType: null,
    configured: true,
    configuredOverride: null,  },
];

export type AgentTemplate = {
  slug: string;
  name: string;
  description: string;
  tags: string[];
  imageUrl: string;
  gradientFrom: string;
  gradientTo: string;
};

export const AGENT_TEMPLATES: AgentTemplate[] = [
  {
    slug: "voice-receptionist",
    name: "Voice Receptionist",
    description: "Answer calls, qualify, and book — 24/7.",
    tags: ["Voice", "Front desk"],
    imageUrl: "/dashboard-assets/agents/voice-receptionist.png",
    gradientFrom: "#0ea5e9",
    gradientTo: "#22d3ee",
  },
  {
    slug: "inbox-triage",
    name: "Inbox & Calendar Triage",
    description: "Inbox zero plus a calendar that protects deep work.",
    tags: ["Productivity", "Schedule"],
    imageUrl: "/dashboard-assets/agents/inbox-triage.png",
    gradientFrom: "#10b981",
    gradientTo: "#84cc16",
  },
];

export type ResourceTile = {
  slug: string;
  title: string;
  description: string;
  category: "tutorial" | "concept" | "troubleshooting" | "update" | "inspiration";
  format: "video" | "article" | "prompt";
  durationMin?: number;
  readMin?: number;
  imageUrl?: string;
};

export const RESOURCES: ResourceTile[] = [
  {
    slug: "first-steps",
    title: "Take your first steps with your OpenClaw",
    description: "A 4-minute tour of what your agent can do today.",
    category: "tutorial",
    format: "video",
    durationMin: 4,
    imageUrl: "/dashboard-assets/resources/first-steps.png",
  },
  {
    slug: "first-workflow",
    title: "Build your first automated workflow by automating your emails",
    description: "Wire your inbox into a daily summary in under 10 minutes.",
    category: "tutorial",
    format: "article",
    readMin: 6,
    imageUrl: "/dashboard-assets/resources/first-workflow.png",
  },
  {
    slug: "what-are-skills",
    title: "What are Skills?",
    description: "The mental model for how OpenClaw learns new tricks.",
    category: "concept",
    format: "article",
    readMin: 5,
    imageUrl: "/dashboard-assets/resources/what-are-skills.png",
  },
  {
    slug: "openclaw-memory",
    title: "Improving your OpenClaw memory",
    description: "Why context decays and the 3 patterns that fix it.",
    category: "concept",
    format: "article",
    readMin: 7,
    imageUrl: "/dashboard-assets/resources/openclaw-memory.png",
  },
  {
    slug: "multi-agent-setup",
    title: "How do I set up multiple agents?",
    description: "Hand off between specialists without losing context.",
    category: "tutorial",
    format: "video",
    durationMin: 8,
    imageUrl: "/dashboard-assets/resources/multi-agent-setup.png",
  },
  {
    slug: "lower-cost",
    title: "Get more from your OpenClaw at lower cost",
    description: "Caching, model routing, and the cheap-eval trick.",
    category: "concept",
    format: "article",
    readMin: 9,
    imageUrl: "/dashboard-assets/resources/lower-cost.png",
  },
  {
    slug: "google-auth-fix",
    title: "Lost authentication to your Google Mail — how to fix",
    description: "Re-pair Workspace in two clicks.",
    category: "troubleshooting",
    format: "article",
    readMin: 3,
    imageUrl: "/dashboard-assets/resources/google-auth-fix.png",
  },
  {
    slug: "integrate-anything",
    title: "How to integrate your OpenClaw with anything",
    description: "Webhooks, MCP, and the no-code escape hatches.",
    category: "tutorial",
    format: "article",
    readMin: 8,
    imageUrl: "/dashboard-assets/resources/integrate-anything.png",
  },
];
